import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@store/types';
import { AirflowDashboardHandler } from '@api/airflow-dashboard-handler';
import { errorAlert } from '@components/toastify/notify-toast';
import { DEFAULT_TIME_RANGE, RECENT_TASK_INSTANCES_CHART_LIMIT, TIME_RANGE_OPTIONS } from '../../constants';
import { bucketTimestamps, hoursAgoRange } from '../../utils';
import { InstanceChartPoint } from './instances-chart';

/*
 * Manages the DAG-task detail's Overview tab: failed-instances stat card and the
 * queued/run-duration chart for the last N instances of this task
 */
export function useOverviewTab(dagId: string, taskId: string, refreshKey: number) {
    const { envVariables: Env } = useSelector((store: RootState) => store.AccountReducer);
    const [timeRange, setTimeRange] = useState(DEFAULT_TIME_RANGE);
    const [failedTotal, setFailedTotal] = useState(0);
    const [failedSeries, setFailedSeries] = useState<{ index: number; count: number }[]>([]);
    const [chartPoints, setChartPoints] = useState<InstanceChartPoint[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const range = useMemo(() => {
        const hours = TIME_RANGE_OPTIONS.find((option) => option.value === timeRange)?.hours ?? 24;
        return hoursAgoRange(hours);
    }, [timeRange]);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);

        const failedPromise = AirflowDashboardHandler.listDagTaskInstances(Env, dagId, {
            limit: 100,
            state: ['failed'],
            runAfterGte: range.gte,
            runAfterLte: range.lte,
            order_by: '-execution_date',
            taskId,
        });
        const chartPromise = AirflowDashboardHandler.listDagTaskInstances(Env, dagId, {
            limit: RECENT_TASK_INSTANCES_CHART_LIMIT,
            order_by: '-execution_date',
            taskId,
        });

        Promise.all([failedPromise, chartPromise])
            .then(([failedResponse, chartResponse]) => {
                if (cancelled) return;
                setFailedTotal(failedResponse.total_entries ?? failedResponse.task_instances.length);
                setFailedSeries(bucketTimestamps(failedResponse.task_instances.map((t) => t.execution_date ?? t.run_after), range.gte, range.lte));

                const points: InstanceChartPoint[] = chartResponse.task_instances
                    .slice()
                    .reverse()
                    .map((instance) => {
                        const startMs = instance.start_date ? new Date(instance.start_date).getTime() : undefined;
                        const endMs = instance.end_date ? new Date(instance.end_date).getTime() : undefined;
                        const queuedMs = instance.queued_when ? new Date(instance.queued_when).getTime() : undefined;
                        const queuedDurationSec = startMs && queuedMs ? Math.max(0, (startMs - queuedMs) / 1000) : 0;
                        const runDurationSec = instance.duration ?? (startMs && endMs ? Math.max(0, (endMs - startMs) / 1000) : 0);
                        return {
                            runId: instance.dag_run_id,
                            label: instance.execution_date ?? instance.run_after ?? instance.start_date,
                            queuedDurationSec,
                            runDurationSec,
                            state: (instance.state as any) ?? 'queued',
                        };
                    });
                setChartPoints(points);
            })
            .catch(() => {
                if (!cancelled) errorAlert('Failed to load task Overview metrics.');
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [Env, dagId, taskId, range, refreshKey]);

    return { timeRange, setTimeRange, failedTotal, failedSeries, chartPoints, isLoading };
}
